import React, { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { UserAuditLog, RoleAuditLog, ParsedAuditData } from '../types/api';

interface AuditLogViewerProps {
  auditLogs: (UserAuditLog | RoleAuditLog)[];
  loading?: boolean;
}

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({ auditLogs, loading = false }) => {
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const toggleExpanded = (auditLogId: string) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(auditLogId)) {
      newExpanded.delete(auditLogId);
    } else {
      newExpanded.add(auditLogId);
    }
    setExpandedLogs(newExpanded);
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
  };

  const parseJsonData = (jsonString: string): ParsedAuditData => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return {};
    }
  };

  const renderFieldChanges = (beforeData: ParsedAuditData, afterData: ParsedAuditData) => {
    const allKeys = new Set([...Object.keys(beforeData), ...Object.keys(afterData)]);
    const changes: Array<{ field: string; before: any; after: any }> = [];

    allKeys.forEach(key => {
      const before = beforeData[key];
      const after = afterData[key];
      
      if (JSON.stringify(before) !== JSON.stringify(after)) {
        changes.push({ field: key, before, after });
      }
    });

    if (changes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No field changes detected
        </Typography>
      );
    }

    return (
      <Box>
        {changes.map((change, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {change.field}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1, 
                    backgroundColor: '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                    Before
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                    {change.before === null || change.before === undefined 
                      ? 'null' 
                      : typeof change.before === 'object' 
                        ? JSON.stringify(change.before, null, 2)
                        : String(change.before)
                    }
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1, 
                    backgroundColor: '#e8f5e8',
                    border: '1px solid #4caf50',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                    After
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                    {change.after === null || change.after === undefined 
                      ? 'null' 
                      : typeof change.after === 'object' 
                        ? JSON.stringify(change.after, null, 2)
                        : String(change.after)
                    }
                  </Typography>
                </Paper>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Loading audit history...
        </Typography>
        {/* You could add skeleton loading here */}
      </Box>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No audit history found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No changes have been recorded for this item yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon />
        Change History ({auditLogs.length} {auditLogs.length === 1 ? 'change' : 'changes'})
      </Typography>
      
      {auditLogs.map((log, index) => {
        const isExpanded = expandedLogs.has(log.auditLogId);
        const beforeData = parseJsonData(log.beforeData);
        const afterData = parseJsonData(log.afterData);
        
        return (
          <Card key={log.auditLogId} sx={{ mb: 2 }}>
            <Accordion 
              expanded={isExpanded} 
              onChange={() => toggleExpanded(log.auditLogId)}
              sx={{ boxShadow: 'none' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={log.action} 
                    color="primary" 
                    size="small"
                    icon={<EditIcon />}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {log.changedByUsername}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(log.changedAt)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                {renderFieldChanges(beforeData, afterData)}
              </AccordionDetails>
            </Accordion>
          </Card>
        );
      })}
    </Box>
  );
};

export default AuditLogViewer;

